import type { EvalScale, IndicatorLevel, IndicatorScale } from '@/interfaces/Scale.interfaces'
import { sortValue } from '@/helpers/Sorting.helpers'

const highValueScale = 100

export const scaleColorIndicator = (score: number, scale: IndicatorScale | null = null): string => {
  const defaultColor = '#eeeeee'

  if (!scale || scale.indicator_levels === void 0 || !scale.indicator_levels || score === null) {
    return defaultColor
  }

  for (let i = 0; i < scale.indicator_levels.length; i++) {
    const current = scale.indicator_levels[i].value

    if (i < scale.indicator_levels.length - 1) {
      const next = scale.indicator_levels[i + 1].value

      if (current !== void 0 && next !== void 0 && score >= current && score < next) {
        return scale.indicator_levels[i].color ?? defaultColor
      }
    } else {
      if (score >= 100 && scale && scale.type !== 'PRIORITY') {
        return scale.color ?? defaultColor
      } else if (current !== void 0 && score >= current && score <= 100) {
        return scale.indicator_levels[i].color ?? defaultColor
      }
    }
  }

  if (score >= 100) {
    return scale.color ?? defaultColor
  }

  return defaultColor
}

export const evalScaleName = (value: number, scale: EvalScale | null | undefined): string => {
  if (!scale || !scale.eval_levels) {
    return ''
  }

  for (const evalLevel of scale.eval_levels) {
    if (value >= evalLevel.low_value && value <= evalLevel.high_value) {
      return evalLevel.name
    }
  }

  return ''
}

export const scaleLevelIndicator = (
  value: number | null,
  scale: IndicatorScale | null = null
): IndicatorLevel | null => {
  if (!scale || !scale.indicator_levels || value === null) {
    return null
  }
  if (scale.type === 'PRIORITY') {
    for (const indicatorLevel of scale.indicator_levels) {
      if (value === indicatorLevel.value) {
        return indicatorLevel
      }
    }
    return null
  } else {
    for (let i = 0; i < scale.indicator_levels.length; i++) {
      if (i < scale.indicator_levels.length - 1) {
        if (value >= scale.indicator_levels[i].value && value < scale.indicator_levels[i + 1].value) {
          return scale.indicator_levels[i]
        }
      } else if (value >= scale.indicator_levels[i].value && value <= 100) {
        return scale.indicator_levels[i]
      }
    }
  }
  return null
}

export const levelNamePriority = (score: number | null, scale: IndicatorScale | null = null): string => {
  const DEFAULT_NAME = ''

  if (score === null || !scale?.indicator_levels?.length) {
    return DEFAULT_NAME
  }

  const hasValue = (obj: IndicatorLevel): obj is IndicatorLevel & { value: number } => typeof obj.value === 'number'

  const levels = scale.indicator_levels.filter(hasValue).sort(sortValue)

  if (!levels.length) {
    return DEFAULT_NAME
  }

  if (score < levels[0].value) {
    return levels[0].name ?? DEFAULT_NAME
  }

  const foundLevel = levels.find((level, i) => {
    const currentValue = level.value
    const nextValue = levels[i + 1]?.value

    if (nextValue !== undefined) {
      return score >= currentValue && score < nextValue
    }
    return score >= currentValue && score <= highValueScale
  })

  return foundLevel?.name ?? DEFAULT_NAME
}

export const levelColorPriority = (score: number | null, scale: IndicatorScale | null = null): string => {
  const DEFAULT_COLOR = '#eeeeee'

  if (score === null || !scale?.indicator_levels?.length) {
    return DEFAULT_COLOR
  }

  const hasValueAndColor = (obj: IndicatorLevel): obj is IndicatorLevel & { value: number; color: string } =>
    typeof obj.value === 'number' && typeof obj.color === 'string'

  const levels = scale.indicator_levels.filter(hasValueAndColor).sort(sortValue)

  if (!levels.length) {
    return DEFAULT_COLOR
  }

  if (score < levels[0].value) {
    return levels[0].color ?? DEFAULT_COLOR
  }

  const foundLevel = levels.find((level, i) => {
    const currentValue = level.value
    const nextValue = levels[i + 1]?.value

    if (nextValue !== undefined) {
      return score >= currentValue && score < nextValue
    }
    return score >= currentValue && score <= highValueScale
  })

  return foundLevel?.color ?? DEFAULT_COLOR
}
